import { useCallback, useMemo, useState } from "react";
import { Box, HStack, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import { MdRefresh } from "react-icons/md";
import { FiAlertTriangle, FiCalendar } from "react-icons/fi";
import { LuList, LuLayers } from "react-icons/lu";
import { useSearchParams } from "react-router";
import AppPage from "layouts/AppPage";
import { AssigneeTaskDrawer } from "features/NewHirePortal/Components";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";
import useCaminoStore from "stores/caminoStore";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useNewHireJourneyTasksQuery } from "features/Resources/Task/hooks";
import { filterMyJourneyTasks } from "features/Resources/Task/helpers";
import TaskCard from "features/Resources/Task/Components/UserTaskSummary/TaskCard";
import TasksByStageView from "features/Resources/Task/Components/UserTaskSummary/TasksByStageView";
import { Task } from "features/Resources/Task/models";
import OverviewSection from "features/NewHireJourney/Components/Overview/OverviewSection";
import LoadingDots from "components/Loading";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";
import FilterButton from "components/Button/FilterButton";
import FilterChipMenu from "components/Button/FilterChipMenu";
import { STAGE_OPTIONS } from "features/Resources/Shared/constants";
import { SegmentedControl } from "design-system/components/SegmentedControl";
import { Heading22 } from "components/Heading";
import EmojiEmptyState from "components/EmojiEmptyState";

const NEW_HIRE_STAGE_OPTIONS = STAGE_OPTIONS.filter(
  (option) => option.value !== "immediate" && option.value !== "dependency",
);

type ViewMode = "list" | "stage";

const VIEW_MODE_OPTIONS = [
  { value: "list" as const, label: "List", icon: LuList },
  { value: "stage" as const, label: "By Stage", icon: LuLayers },
];

type StatusFilterMode = "all" | "active" | "closed" | "upcoming" | "overdue" | "due_soon";

interface TaskFilters {
  q?: string;
  status_filter?: StatusFilterMode;
  stage_key?: string;
}

const defaultFilters: TaskFilters = {
  status_filter: "active",
};

const SORT_OPTIONS: SortOption[] = [
  { label: "Due Date: Earliest First", value: "due_date_asc" },
  { label: "Due Date: Latest First", value: "due_date_desc" },
];

const DEFAULT_SORT = "due_date_asc";

const TASK_EMPTY_STATES = [
  { emoji: "✅", line1: "All caught up", line2: "A great time to check your upcoming meetings" },
  { emoji: "🌟", line1: "Nothing here", line2: "You're on top of your onboarding" },
  { emoji: "☀️", line1: "Clear queue", line2: "A good time to ask questions or review your notes" },
  { emoji: "🎉", line1: "No tasks right now", line2: "Take a moment to celebrate your progress" },
];

function TasksEmptyState({ hasVisibleFilters }: { hasVisibleFilters: boolean }) {
  const entry = hasVisibleFilters
    ? { emoji: "🔍", line1: "No matching tasks", line2: "Try adjusting your filters" }
    : TASK_EMPTY_STATES[new Date().getDay() % TASK_EMPTY_STATES.length];
  return <EmojiEmptyState {...entry} />;
}

export default function NewHireTasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [currentSort, setCurrentSort] = useState(DEFAULT_SORT);
  const currentUser = useCaminoStore((state) => state.currentUser);
  const { data: journey, isLoading: isLoadingJourney } = useNewHireJourneyDetailQuery(currentUser?.newHireJourneyId);
  const { data: tasks = [] } = useNewHireJourneyTasksQuery(journey?.id || "", { enabled: !!journey?.id });

  const filters = useMemo((): TaskFilters => {
    const params: TaskFilters = {};
    const q = searchParams.get("q");
    const statusFilter = searchParams.get("status_filter");
    const stageKey = searchParams.get("stage_key");

    if (q) params.q = q;
    if (statusFilter) params.status_filter = statusFilter as StatusFilterMode;
    if (stageKey) params.stage_key = stageKey;

    if (!statusFilter && !stageKey && !q) {
      return defaultFilters;
    }

    return { ...defaultFilters, ...params };
  }, [searchParams]);

  const selectedStage = useMemo(() => {
    if (!filters.stage_key) return null;
    return NEW_HIRE_STAGE_OPTIONS.find((option) => option.value === filters.stage_key) || null;
  }, [filters.stage_key]);

  const handleTaskClick = useCallback(
    (task: Task) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("task_id", task.id);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const handleCloseDrawer = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("task_id");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const selectedTaskId = searchParams.get("task_id");

  const handleFilterChange = useCallback(
    (key: keyof TaskFilters, value: string | undefined) => {
      const newValue = value === "" ? undefined : value;
      const newParams = new URLSearchParams(searchParams);
      if (newValue !== undefined && newValue !== "") {
        newParams.set(key, newValue);
      } else {
        newParams.delete(key);
      }
      newParams.delete("task_action");
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const handleReset = useCallback(() => {
    const newParams = new URLSearchParams();
    newParams.set("status_filter", defaultFilters.status_filter || "active");
    setSearchParams(newParams);
  }, [setSearchParams]);

  if (isLoadingJourney) {
    return (
      <AppPage>
        <LoadingDots />
      </AppPage>
    );
  }

  if (!journey) {
    return (
      <AppPage>
        <CDS.Text14 color="content.tertiary">No journey found</CDS.Text14>
      </AppPage>
    );
  }

  const myTasks = filterMyJourneyTasks(tasks, currentUser?.id, journey?.id);
  const nonUpcomingTasks = myTasks.filter((t) => t.status !== "upcoming");
  const openTasksCount = nonUpcomingTasks.filter((t) => ["active", "in_progress", "blocked"].includes(t.status)).length;

  let filteredTasks = [...nonUpcomingTasks];

  if (filters.q) {
    const searchTerm = filters.q.toLowerCase();
    filteredTasks = filteredTasks.filter(
      (task) => task.name.toLowerCase().includes(searchTerm) || task.description?.toLowerCase().includes(searchTerm),
    );
  }

  if (filters.stage_key) {
    if (filters.stage_key === "preboarding") {
      filteredTasks = filteredTasks.filter((task) => task.stageKey === "preboarding" || task.stageKey === "immediate");
    } else {
      filteredTasks = filteredTasks.filter((task) => task.stageKey === filters.stage_key);
    }
  }

  const statusFilter = filters.status_filter || "all";
  if (statusFilter === "active") {
    filteredTasks = filteredTasks.filter((task) =>
      ["active", "in_progress", "blocked", "failed"].includes(task.status),
    );
  } else if (statusFilter === "closed") {
    // show all — no additional filter, open tasks remain visible alongside closed ones
  } else if (statusFilter === "upcoming") {
    filteredTasks = filteredTasks.filter((task) => task.status === "upcoming");
  } else if (statusFilter === "overdue") {
    filteredTasks = filteredTasks.filter((task) => task.isOverdue);
  } else if (statusFilter === "due_soon") {
    filteredTasks = filteredTasks.filter((task) => task.isUrgent && !task.isOverdue);
  }

  filteredTasks.sort((a, b) => {
    const aDate = a.dueOn ? new Date(a.dueOn).getTime() : Infinity;
    const bDate = b.dueOn ? new Date(b.dueOn).getTime() : Infinity;
    return currentSort === "due_date_desc" ? bDate - aDate : aDate - bDate;
  });

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === "status_filter") return value !== undefined && value !== "" && value !== "all";
    return value !== undefined && value !== "";
  });

  const activeFilters: ActiveFilter[] = [];

  if (filters.status_filter === "closed") {
    activeFilters.push({
      key: "status_filter",
      label: "Including Closed",
      onRemove: () => handleFilterChange("status_filter", "active"),
    });
  } else if (filters.status_filter === "overdue") {
    activeFilters.push({
      key: "status_filter",
      label: "Overdue",
      onRemove: () => handleFilterChange("status_filter", "active"),
    });
  } else if (filters.status_filter === "due_soon") {
    activeFilters.push({
      key: "status_filter",
      label: "Due Soon",
      onRemove: () => handleFilterChange("status_filter", "active"),
    });
  }

  if (filters.stage_key) {
    activeFilters.push({
      key: "stage",
      label: `Stage: ${selectedStage?.label || "Unknown"}`,
      onRemove: () => handleFilterChange("stage_key", undefined),
    });
  }

  const taskSubtitle =
    statusFilter === "closed"
      ? `Showing ${filteredTasks.length} Tasks`
      : `Showing ${filteredTasks.length} of ${openTasksCount} Open Tasks`;

  return (
    <AppPage>
      <VStack spacing={6} align="stretch" pb={8}>
        <HStack justify="space-between" align="center">
          <Heading22>My Tasks</Heading22>
          <SegmentedControl options={VIEW_MODE_OPTIONS} value={viewMode} onChange={setViewMode} />
        </HStack>

        {/* Filters — only shown in list mode */}
        {viewMode === "list" && <FilterSortSearchBar
          searchPlaceholder="Search by task name..."
          searchValue={filters.q}
          onSearchChange={(value) => handleFilterChange("q", value)}
          currentSort={currentSort}
          defaultSort={DEFAULT_SORT}
          onSortChange={setCurrentSort}
          sortOptions={SORT_OPTIONS}
          quickFilters={
            <>
              <FilterButton
                onClick={() =>
                  handleFilterChange("status_filter", filters.status_filter === "closed" ? "active" : "closed")
                }
                isActive={filters.status_filter === "closed"}
                icon={MdRefresh}
                size="sm"
              >
                Show Closed
              </FilterButton>
              <FilterButton
                onClick={() =>
                  handleFilterChange("status_filter", filters.status_filter === "overdue" ? undefined : "overdue")
                }
                isActive={filters.status_filter === "overdue"}
                icon={FiAlertTriangle}
                size="sm"
              >
                Overdue
              </FilterButton>
              <FilterButton
                onClick={() =>
                  handleFilterChange("status_filter", filters.status_filter === "due_soon" ? undefined : "due_soon")
                }
                isActive={filters.status_filter === "due_soon"}
                icon={FiCalendar}
                size="sm"
              >
                Due Soon
              </FilterButton>
              <FilterChipMenu
                label="Stage"
                options={NEW_HIRE_STAGE_OPTIONS}
                value={filters.stage_key}
                onChange={(next) => handleFilterChange("stage_key", next)}
              />
            </>
          }
          activeFilters={activeFilters}
          onReset={handleReset}
          hasActiveFilters={hasActiveFilters}
        />}

        {/* Stage mode filters */}
        {viewMode === "stage" && (
          <FilterSortSearchBar
            searchPlaceholder=""
            searchValue=""
            onSearchChange={() => {}}
            currentSort="default"
            defaultSort="default"
            onSortChange={() => {}}
            sortOptions={[]}
            hideSort
            quickFilters={
              <FilterButton
                onClick={() => setHideCompleted((v) => !v)}
                isActive={hideCompleted}
                size="sm"
              >
                Hide Completed
              </FilterButton>
            }
            activeFilters={[
              ...(hideCompleted ? [{ key: "hideCompleted", label: "Hiding Completed", onRemove: () => setHideCompleted(false) }] : []),
            ]}
            onReset={() => { setHideCompleted(false); }}
            hasActiveFilters={hideCompleted}
          />
        )}

        {/* Task list */}
        {viewMode === "stage" ? (
          <TasksByStageView tasks={myTasks} journey={journey} hideUpcoming hideCompleted={hideCompleted} onTaskClick={handleTaskClick} />
        ) : (
          <OverviewSection title="My Tasks" subtitle={taskSubtitle}>
            {filteredTasks.length === 0 ? (
              <TasksEmptyState hasVisibleFilters={activeFilters.length > 0 || !!filters.q} />
            ) : (
              <Box>
                {filteredTasks.map((task) => (
                  <TaskCard key={task.id} task={task} compact showAssignee={false} onClick={handleTaskClick} />
                ))}
              </Box>
            )}
          </OverviewSection>
        )}
      </VStack>

      <AssigneeTaskDrawer isOpen={!!selectedTaskId} onClose={handleCloseDrawer} taskId={selectedTaskId} />
      <TaskActionModalByQueryParam />
    </AppPage>
  );
}
